import React, { useState, useEffect } from 'react';
import { fetchAdminHomePageContent, updateHomePageContent } from '../services/api';
import LoadingSpinner from './LoadingSpinner';

const TempHomePageContentManager = () => {
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [activeSection, setActiveSection] = useState('hero'); // Track which section is being edited

  useEffect(() => {
    loadHomePageContent();
  }, []);

  const loadHomePageContent = async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      
      // Always try to load from the real API - no mock data
      console.log('Loading homepage content from API...');
      const data = await fetchAdminHomePageContent();
      
      if (data) {
        // Merge with default content to ensure all required fields are present
        const defaultContent = getDefaultContent();
        const mergedContent = {
          ...defaultContent,
          ...data,
          howItWorks: {
            ...defaultContent.howItWorks,
            ...data.howItWorks,
            steps: data.howItWorks?.steps || defaultContent.howItWorks.steps
          },
          pricing: {
            ...defaultContent.pricing,
            ...data.pricing,
            tiers: data.pricing?.tiers || defaultContent.pricing.tiers
          },
          footer: {
            ...defaultContent.footer,
            ...data.footer,
            brand: {
              ...defaultContent.footer.brand,
              ...data.footer?.brand
            },
            sections: data.footer?.sections || defaultContent.footer.sections,
            socialLinks: data.footer?.socialLinks || data.footer?.social || defaultContent.footer.socialLinks
          }
        };
        
        setContent(mergedContent);
        console.log('Homepage content loaded and merged with defaults:', mergedContent);
        console.log('How It Works data:', mergedContent.howItWorks);
        console.log('Pricing data:', mergedContent.pricing);
      } else {
        // If no content exists, initialize with proper structure
        const defaultContent = getDefaultContent();
        setContent(defaultContent);
        console.log('No existing content found, initialized with default structure');
      }
    } catch (error) {
      console.error('Failed to load home page content:', error);
      
      // Provide specific error messages for different scenarios
      if (error.message.includes('Authentication required')) {
        setErrorMessage('Authentication required. Please log in to the admin panel again.');
      } else if (error.message.includes('not yet implemented')) {
        setErrorMessage('Admin homepage management is not yet implemented in the backend. Using local storage for development.');
      } else if (error.message.includes('backend homepage management not available')) {
        setErrorMessage('Backend homepage management not available. Content will be saved locally for development.');
      } else {
        setErrorMessage(`Failed to load home page content: ${error.message || 'Unknown error'}`);
      }
      
      // Initialize with proper structure even on error so the form can be used
      const defaultContent = getDefaultContent();
      setContent(defaultContent);
    } finally {
      setIsLoading(false);
    }
  };

  const getDefaultContent = () => ({
    hero: {
      title: 'Your links',
      subtitle: 'beautifully organized',
      description: 'Create a stunning link-in-bio page in minutes. Share all your important links from one beautiful, customizable page.',
      ctaText: 'Get Started Free',
      ctaLink: '/signup',
      imageUrl: '/images/hero.png'
    },
    features: [
      { id: "feature-1", icon: '🎨', title: 'Fully Customizable', description: 'Personalize your page with custom colors, fonts, and layouts to match your brand.' },
      { id: "feature-2", icon: '📱', title: 'Mobile Optimized', description: 'Your page looks perfect on any device, ensuring the best experience for your audience.' },
      { id: "feature-3", icon: '📊', title: 'Analytics Dashboard', description: 'Track clicks, views, and engagement to understand your audience better.' }
    ],
    howItWorks: {
      title: 'Get started in',
      subtitle: '3 simple steps',
      description: 'Creating your link-in-bio page takes just minutes.',
      ctaText: 'Start Building Your Page',
      ctaLink: '/signup',
      steps: [
        { id: "step-1", number: '01', title: 'Sign Up', description: 'Create your free account in seconds. No credit card required.' },
        { id: "step-2", number: '02', title: 'Customize', description: 'Add your links, upload images, and personalize your page design.' },
        { id: "step-3", number: '03', title: 'Share', description: 'Get your unique URL and share it everywhere - social media, email, anywhere!' }
      ]
    },
    pricing: {
      title: 'Simple, transparent pricing',
      description: 'Choose the perfect plan for your needs.',
      tiers: [
        { id: "free", name: "Free", price: "0", description: "Perfect for getting started", features: ["Unlimited links", "2 pre-built themes", "5-day basic analytics"], cta: "Get Started", ctaLink: "/signup", popular: false },
        { id: "pro", name: "Pro", price: "2", description: "Most popular choice", features: ["Everything in Free", "5 themes + color customization", "30-day detailed analytics"], cta: "Start Pro Trial", ctaLink: "/signup?plan=pro", popular: true },
        { id: "max", name: "Max", price: "4", description: "For power users", features: ["Everything in Pro", "Full CSS customization", "Lifetime analytics with exports"], cta: "Start Max Trial", ctaLink: "/signup?plan=max", popular: false }
      ]
    },
    cta: {
      title: 'Ready to build your link-in-bio page?',
      description: 'Join thousands of creators, entrepreneurs, and professionals.',
      primaryCtaText: 'Get Started Free',
      primaryCtaLink: '/signup',
      secondaryCtaText: 'View Demo',
      secondaryCtaLink: '#demo'
    },
    footer: {
      brand: {
        name: 'Link Spaghetti',
        description: 'The easiest way to share all your important links in one beautiful page.',
        logo: '/logo.svg'
      },
      copyright: '© 2024 Link Spaghetti. All rights reserved.',
      sections: [
        { 
          title: 'Product', 
          links: [
            { name: 'Features', text: 'Features', url: '#features' }, 
            { name: 'Pricing', text: 'Pricing', url: '#pricing' }, 
            { name: 'Templates', text: 'Templates', url: '#' }
          ] 
        },
        { 
          title: 'Company', 
          links: [
            { name: 'About', text: 'About', url: '#' }, 
            { name: 'Blog', text: 'Blog', url: '#' }, 
            { name: 'Careers', text: 'Careers', url: '#' }
          ] 
        },
        { 
          title: 'Support', 
          links: [
            { name: 'Help Center', text: 'Help Center', url: '#' }, 
            { name: 'Contact Us', text: 'Contact Us', url: '#' }, 
            { name: 'Status', text: 'Status', url: '#' }
          ] 
        }
      ],
      socialLinks: [
        { platform: 'facebook', name: 'facebook', url: '#' },
        { platform: 'twitter', name: 'twitter', url: '#' },
        { platform: 'instagram', name: 'instagram', url: '#' }
      ]
    },
    // Backend-compatible sections
    settings: {
      theme: 'light',
      fontFamily: 'Inter',
      customCSS: '',
      analytics: true,
      showPricing: true,
      seoTitle: 'QuickLinks - Your Link-in-Bio Page',
      seoDescription: 'Create a stunning link-in-bio page in minutes.',
      favicon: '/favicon.ico',
      maintenanceMode: false
    },
    navbar: {
      logo: '/logo.svg',
      links: [
        { name: 'Features', text: 'Features', url: '#features' },
        { name: 'Pricing', text: 'Pricing', url: '#pricing' },
        { name: 'Login', text: 'Login', url: '/signin' },
        { name: 'Sign Up', text: 'Sign Up', url: '/signup' }
      ],
      showAuth: true
    }
  });

  const saveContent = async () => {
    setIsSaving(true);
    setSaveMessage('');
    setErrorMessage('');
    
    try {
      console.log('Saving homepage content to API:', content);
      
      // Always use real API - no mock/dev mode
      const response = await updateHomePageContent(content);
      console.log('Content saved successfully:', response);
      
      // Check if the response indicates localStorage fallback was used
      if (response.message && response.message.includes('saved locally')) {
        setSaveMessage(`✅ ${response.message}`);
      } else {
        setSaveMessage(response.message || 'Home page content updated successfully');
      }
    } catch (error) {
      console.error('Failed to update content:', error);
      
      // Provide specific error messages for different scenarios
      if (error.message.includes('Authentication required') || error.message.includes('authentication failed')) {
        setErrorMessage('Authentication failed. Please log in to the admin panel again.');
      } else if (error.message.includes('not yet implemented')) {
        setErrorMessage('Admin homepage management is not yet implemented in the backend. Content saved locally for development.');
      } else {
        setErrorMessage(`Failed to update content: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Update section handlers
  const updateSection = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateHero = (field, value) => {
    updateSection('hero', field, value);
  };

  // Array update handlers
  const updateFeature = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      features: prev.features.map((feature, i) => 
        i === index ? { ...feature, [field]: value } : feature
      )
    }));
  };

  const addFeature = () => {
    setContent(prev => ({
      ...prev,
      features: [...prev.features, { id: `feature-${Date.now()}`, icon: '⭐', title: '', description: '' }]
    }));
  };

  const removeFeature = (index) => {
    setContent(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const updateStep = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      howItWorks: {
        ...prev.howItWorks,
        steps: prev.howItWorks.steps.map((step, i) => 
          i === index ? { ...step, [field]: value } : step
        )
      }
    }));
  };

  const updatePricingTier = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        tiers: prev.pricing.tiers.map((tier, i) => 
          i === index ? { ...tier, [field]: value } : tier
        )
      }
    }));
  };

  const updateTierFeature = (tierIndex, featureIndex, value) => {
    setContent(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        tiers: prev.pricing.tiers.map((tier, i) => 
          i === tierIndex ? {
            ...tier,
            features: tier.features.map((feature, j) => 
              j === featureIndex ? value : feature
            )
          } : tier
        )
      }
    }));
  };

  const addTierFeature = (tierIndex) => {
    setContent(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        tiers: prev.pricing.tiers.map((tier, i) => 
          i === tierIndex ? {
            ...tier,
            features: [...tier.features, '']
          } : tier
        )
      }
    }));
  };

  const removeTierFeature = (tierIndex, featureIndex) => {
    setContent(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        tiers: prev.pricing.tiers.map((tier, i) => 
          i === tierIndex ? {
            ...tier,
            features: tier.features.filter((_, j) => j !== featureIndex)
          } : tier
        )
      }
    }));
  };

  const addPricingTier = () => {
    const newTier = {
      id: `tier-${Date.now()}`,
      name: '',
      price: '',
      description: '',
      features: [],
      cta: 'Get Started',
      ctaLink: '/signup',
      popular: false
    };
    
    setContent(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        tiers: [...(prev.pricing.tiers || []), newTier]
      }
    }));
  };

  const removePricingTier = (index) => {
    setContent(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        tiers: prev.pricing.tiers.filter((_, i) => i !== index)
      }
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner />
        <span className="ml-2 text-gray-600">Loading home page content...</span>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="p-8 text-center text-gray-600">
        <p>Failed to load home page content. {errorMessage?.includes('401') ? 'Authentication required.' : 'Please try again.'}</p>
        <div className="mt-4 flex justify-center space-x-4">
          <button 
            onClick={loadHomePageContent}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm"
          >
            Retry
          </button>
          
          {errorMessage?.includes('401') && (
            <a 
              href="/admin/login" 
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm"
            >
              Log In Again
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {saveMessage && (
        <div className="mb-4 p-2 bg-green-100 border border-green-300 text-green-700 rounded">
          {saveMessage}
        </div>
      )}
      
      {errorMessage && (
        <div className="mb-4 p-2 bg-red-100 border border-red-300 text-red-700 rounded">
          {errorMessage}
        </div>
      )}

      {/* Section Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex flex-wrap gap-x-8 gap-y-2">
          {[
            { id: 'hero', name: 'Hero Section', icon: '🎯' },
            { id: 'features', name: 'Features', icon: '⭐' },
            { id: 'howItWorks', name: 'How It Works', icon: '🔧' },
            { id: 'pricing', name: 'Pricing', icon: '💰' },
            { id: 'cta', name: 'Call to Action', icon: '🚀' },
            { id: 'footer', name: 'Footer', icon: '📄' },
            { id: 'settings', name: 'Settings', icon: '⚙️' },
            { id: 'navbar', name: 'Navigation', icon: '🧭' }
          ].map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`${
                activeSection === section.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
            >
              <span>{section.icon}</span>
              <span>{section.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Section Content */}
      <div className="space-y-6">
        {activeSection === 'hero' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Hero Section</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={content?.hero?.title || ''}
                onChange={(e) => updateHero('title', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="Hero title"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
              <input
                type="text"
                value={content?.hero?.subtitle || ''}
                onChange={(e) => updateHero('subtitle', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="Hero subtitle"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={content?.hero?.description || ''}
                onChange={(e) => updateHero('description', e.target.value)}
                rows={4}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="Hero description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CTA Text</label>
              <input
                type="text"
                value={content?.hero?.ctaText || ''}
                onChange={(e) => updateHero('ctaText', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="Call to action button text"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CTA Link</label>
              <input
                type="text"
                value={content?.hero?.ctaLink || ''}
                onChange={(e) => updateHero('ctaLink', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="/signup"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hero Image URL</label>
              <input
                type="text"
                value={content?.hero?.imageUrl || ''}
                onChange={(e) => updateHero('imageUrl', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="/images/hero.png"
              />
            </div>
          </div>
        )}

        {activeSection === 'features' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Features Section</h3>
              <button
                onClick={addFeature}
                className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
              >
                + Add Feature
              </button>
            </div>
            
            <div className="space-y-4">
              {content?.features?.map((feature, index) => (
                <div key={feature.id || index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-medium text-gray-800">Feature {index + 1}</h4>
                    <button
                      onClick={() => removeFeature(index)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                      <input
                        type="text"
                        value={feature.icon || ''}
                        onChange={(e) => updateFeature(index, 'icon', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        placeholder="🎨"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <input
                        type="text"
                        value={feature.title || ''}
                        onChange={(e) => updateFeature(index, 'title', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        placeholder="Feature title"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={feature.description || ''}
                      onChange={(e) => updateFeature(index, 'description', e.target.value)}
                      rows={3}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      placeholder="Feature description"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeSection === 'howItWorks' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">How It Works Section</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={content?.howItWorks?.title || ''}
                  onChange={(e) => updateSection('howItWorks', 'title', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  placeholder="Get started in"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                <input
                  type="text"
                  value={content?.howItWorks?.subtitle || ''}
                  onChange={(e) => updateSection('howItWorks', 'subtitle', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  placeholder="3 simple steps"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={content?.howItWorks?.description || ''}
                onChange={(e) => updateSection('howItWorks', 'description', e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="Section description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CTA Text</label>
                <input
                  type="text"
                  value={content?.howItWorks?.ctaText || ''}
                  onChange={(e) => updateSection('howItWorks', 'ctaText', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  placeholder="Start Building Your Page"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CTA Link</label>
                <input
                  type="text"
                  value={content?.howItWorks?.ctaLink || ''}
                  onChange={(e) => updateSection('howItWorks', 'ctaLink', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  placeholder="/signup"
                />
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium text-gray-800">Steps</h4>
              {(content?.howItWorks?.steps && Array.isArray(content.howItWorks.steps) && content.howItWorks.steps.length > 0) ? (
                content.howItWorks.steps.map((step, index) => (
                  <div key={step.id || `step-${index}`} className="border border-gray-200 rounded-lg p-4">
                    <h5 className="font-medium text-gray-800 mb-3">Step {index + 1}</h5>
                  
                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Number</label>
                      <input
                        type="text"
                        value={step.number || ''}
                        onChange={(e) => updateStep(index, 'number', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        placeholder="01"
                      />
                    </div>
                    
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <input
                        type="text"
                        value={step.title || ''}
                        onChange={(e) => updateStep(index, 'title', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        placeholder="Step title"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={step.description || ''}
                      onChange={(e) => updateStep(index, 'description', e.target.value)}
                      rows={2}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      placeholder="Step description"
                    />
                  </div>
                </div>
              )) ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No steps defined. Please check your content configuration.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeSection === 'pricing' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Pricing Section</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={content?.pricing?.title || ''}
                onChange={(e) => updateSection('pricing', 'title', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="Simple, transparent pricing"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={content?.pricing?.description || ''}
                onChange={(e) => updateSection('pricing', 'description', e.target.value)}
                rows={2}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="Choose the perfect plan for your needs."
              />
            </div>

            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h4 className="font-medium text-gray-800">Pricing Tiers</h4>
                <button
                  onClick={addPricingTier}
                  className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-sm"
                >
                  + Add Pricing Tier
                </button>
              </div>
              {(content?.pricing?.tiers && Array.isArray(content.pricing.tiers) && content.pricing.tiers.length > 0) ? (
                content.pricing.tiers.map((tier, index) => (
                  <div key={tier.id || `tier-${index}`} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-4">
                      <h5 className="font-medium text-gray-800">{tier.name || `Tier ${index + 1}`}</h5>
                      <div className="flex items-center space-x-3">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={tier.popular || false}
                            onChange={(e) => updatePricingTier(index, 'popular', e.target.checked)}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-600">Popular</span>
                        </label>
                        <button
                          onClick={() => removePricingTier(index)}
                          className="text-red-500 hover:text-red-700 text-sm px-2 py-1 border border-red-300 rounded hover:bg-red-50"
                        >
                          Remove Tier
                        </button>
                      </div>
                    </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={tier.name || ''}
                        onChange={(e) => updatePricingTier(index, 'name', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        placeholder="Free"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                      <input
                        type="text"
                        value={tier.price || ''}
                        onChange={(e) => updatePricingTier(index, 'price', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <input
                      type="text"
                      value={tier.description || ''}
                      onChange={(e) => updatePricingTier(index, 'description', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      placeholder="Perfect for getting started"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CTA Text</label>
                      <input
                        type="text"
                        value={tier.cta || ''}
                        onChange={(e) => updatePricingTier(index, 'cta', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        placeholder="Get Started"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CTA Link</label>
                      <input
                        type="text"
                        value={tier.ctaLink || ''}
                        onChange={(e) => updatePricingTier(index, 'ctaLink', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                        placeholder="/signup"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">Features</label>
                      <button
                        onClick={() => addTierFeature(index)}
                        className="text-blue-500 hover:text-blue-700 text-sm"
                      >
                        + Add Feature
                      </button>
                    </div>
                    <div className="space-y-2">
                      {tier.features?.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex gap-2">
                          <input
                            type="text"
                            value={feature}
                            onChange={(e) => updateTierFeature(index, featureIndex, e.target.value)}
                            className="flex-1 border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                            placeholder="Feature description"
                          />
                          <button
                            onClick={() => removeTierFeature(index, featureIndex)}
                            className="text-red-500 hover:text-red-700 px-2"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )) ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No pricing tiers available. Please check your content configuration.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeSection === 'cta' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Call to Action Section</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={content?.cta?.title || ''}
                onChange={(e) => updateSection('cta', 'title', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="CTA section title"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={content?.cta?.description || ''}
                onChange={(e) => updateSection('cta', 'description', e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="CTA section description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Button Text</label>
                <input
                  type="text"
                  value={content?.cta?.primaryCtaText || ''}
                  onChange={(e) => updateSection('cta', 'primaryCtaText', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  placeholder="Get Started Free"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Button Link</label>
                <input
                  type="text"
                  value={content?.cta?.primaryCtaLink || ''}
                  onChange={(e) => updateSection('cta', 'primaryCtaLink', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  placeholder="/signup"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Button Text</label>
                <input
                  type="text"
                  value={content?.cta?.secondaryCtaText || ''}
                  onChange={(e) => updateSection('cta', 'secondaryCtaText', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  placeholder="View Demo"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Button Link</label>
                <input
                  type="text"
                  value={content?.cta?.secondaryCtaLink || ''}
                  onChange={(e) => updateSection('cta', 'secondaryCtaLink', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  placeholder="#demo"
                />
              </div>
            </div>
          </div>
        )}

        {activeSection === 'footer' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Footer Section</h3>
            
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-gray-800 mb-3">Brand Information</h4>
              
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
                  <input
                    type="text"
                    value={content?.footer?.brand?.name || ''}
                    onChange={(e) => setContent(prev => ({
                      ...prev,
                      footer: {
                        ...prev.footer,
                        brand: { ...prev.footer.brand, name: e.target.value }
                      }
                    }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                    placeholder="Link Spaghetti"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
                  <input
                    type="text"
                    value={content?.footer?.brand?.logo || ''}
                    onChange={(e) => setContent(prev => ({
                      ...prev,
                      footer: {
                        ...prev.footer,
                        brand: { ...prev.footer.brand, logo: e.target.value }
                      }
                    }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                    placeholder="/logo.svg"
                  />
                </div>
              </div>
              
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Tagline/Description</label>
                <textarea
                  value={content?.footer?.brand?.description || ''}
                  onChange={(e) => setContent(prev => ({
                    ...prev,
                    footer: {
                      ...prev.footer,
                      brand: { ...prev.footer.brand, description: e.target.value }
                    }
                  }))}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  placeholder="The easiest way to share all your important links in one beautiful page."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Copyright Text</label>
                <input
                  type="text"
                  value={content?.footer?.copyright || `© ${new Date().getFullYear()} ${content?.footer?.brand?.name || 'Link Spaghetti'}. All rights reserved.`}
                  onChange={(e) => setContent(prev => ({
                    ...prev,
                    footer: {
                      ...prev.footer,
                      copyright: e.target.value
                    }
                  }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                  placeholder="© 2024 Link Spaghetti. All rights reserved."
                />
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-gray-800 mb-3">Footer Sections</h4>
              
              {(content?.footer?.sections || []).map((section, sectionIndex) => (
                <div key={sectionIndex} className="border border-gray-100 rounded-md p-3 mb-3 bg-gray-50">
                  <div className="flex justify-between items-center mb-2">
                    <h5 className="font-medium text-gray-700">Section {sectionIndex + 1}</h5>
                    <button
                      onClick={() => {
                        console.log('Removing footer section at index:', sectionIndex);
                        console.log('Current sections:', content?.footer?.sections);
                        const newSections = [...(content?.footer?.sections || [])];
                        newSections.splice(sectionIndex, 1);
                        console.log('New sections after removal:', newSections);
                        setContent(prev => ({
                          ...prev,
                          footer: { ...prev.footer, sections: newSections }
                        }));
                      }}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                  
                  <div className="mb-2">
                    <input
                      type="text"
                      value={section.title || ''}
                      onChange={(e) => {
                        const newSections = [...(content?.footer?.sections || [])];
                        newSections[sectionIndex] = { ...newSections[sectionIndex], title: e.target.value };
                        setContent(prev => ({
                          ...prev,
                          footer: { ...prev.footer, sections: newSections }
                        }));
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                      placeholder="Section Title (e.g., Product)"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <h6 className="text-sm font-medium text-gray-600">Links</h6>
                    {(section.links || []).map((link, linkIndex) => (
                      <div key={linkIndex} className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={link.name || link.text || ''}
                          onChange={(e) => {
                            const newSections = [...(content?.footer?.sections || [])];
                            const newLinks = [...(newSections[sectionIndex].links || [])];
                            newLinks[linkIndex] = { ...newLinks[linkIndex], name: e.target.value, text: e.target.value };
                            newSections[sectionIndex] = { ...newSections[sectionIndex], links: newLinks };
                            setContent(prev => ({
                              ...prev,
                              footer: { ...prev.footer, sections: newSections }
                            }));
                          }}
                          className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                          placeholder="Link text"
                        />
                        <input
                          type="text"
                          value={link.url || ''}
                          onChange={(e) => {
                            const newSections = [...(content?.footer?.sections || [])];
                            const newLinks = [...(newSections[sectionIndex].links || [])];
                            newLinks[linkIndex] = { ...newLinks[linkIndex], url: e.target.value };
                            newSections[sectionIndex] = { ...newSections[sectionIndex], links: newLinks };
                            setContent(prev => ({
                              ...prev,
                              footer: { ...prev.footer, sections: newSections }
                            }));
                          }}
                          className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                          placeholder="URL"
                        />
                        <button
                          onClick={() => {
                            console.log('Deleting footer section link:', sectionIndex, linkIndex);
                            console.log('Current sections:', content?.footer?.sections);
                            const newSections = [...(content?.footer?.sections || [])];
                            const newLinks = [...(newSections[sectionIndex].links || [])];
                            newLinks.splice(linkIndex, 1);
                            newSections[sectionIndex] = { ...newSections[sectionIndex], links: newLinks };
                            console.log('New sections after deletion:', newSections);
                            setContent(prev => ({
                              ...prev,
                              footer: { ...prev.footer, sections: newSections }
                            }));
                          }}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        const newSections = [...(content?.footer?.sections || [])];
                        const newLinks = [...(newSections[sectionIndex].links || [])];
                        newLinks.push({ name: '', url: '' });
                        newSections[sectionIndex] = { ...newSections[sectionIndex], links: newLinks };
                        setContent(prev => ({
                          ...prev,
                          footer: { ...prev.footer, sections: newSections }
                        }));
                      }}
                      className="text-blue-600 hover:text-blue-700 text-sm"
                    >
                      + Add Link
                    </button>
                  </div>
                </div>
              ))}
              
              <button
                onClick={() => {
                  const newSections = [...(content?.footer?.sections || [])];
                  newSections.push({ title: '', links: [] });
                  setContent(prev => ({
                    ...prev,
                    footer: { ...prev.footer, sections: newSections }
                  }));
                }}
                className="w-full bg-blue-100 text-blue-700 py-2 px-4 rounded-md hover:bg-blue-200 text-sm"
              >
                + Add Footer Section
              </button>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-800 mb-3">Social Links</h4>
              
              {(content?.footer?.socialLinks || content?.footer?.social || []).map((social, index) => (
                <div key={index} className="flex gap-2 items-center mb-2">
                  <input
                    type="text"
                    value={social.platform || social.name || ''}
                    onChange={(e) => {
                      const newSocial = [...(content?.footer?.socialLinks || content?.footer?.social || [])];
                      newSocial[index] = { ...newSocial[index], platform: e.target.value, name: e.target.value };
                      setContent(prev => ({
                        ...prev,
                        footer: { ...prev.footer, socialLinks: newSocial, social: newSocial }
                      }));
                    }}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                    placeholder="Platform (e.g., facebook, twitter)"
                  />
                  <input
                    type="text"
                    value={social.url || ''}
                    onChange={(e) => {
                      const newSocial = [...(content?.footer?.socialLinks || content?.footer?.social || [])];
                      newSocial[index] = { ...newSocial[index], url: e.target.value };
                      setContent(prev => ({
                        ...prev,
                        footer: { ...prev.footer, socialLinks: newSocial, social: newSocial }
                      }));
                    }}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                    placeholder="URL"
                  />
                  <button
                    onClick={() => {
                      console.log('Deleting social link at index:', index);
                      console.log('Current social links:', content?.footer?.socialLinks || content?.footer?.social || []);
                      const currentSocial = content?.footer?.socialLinks || content?.footer?.social || [];
                      const newSocial = [...currentSocial];
                      newSocial.splice(index, 1);
                      console.log('New social links after deletion:', newSocial);
                      setContent(prev => ({
                        ...prev,
                        footer: { 
                          ...prev.footer, 
                          socialLinks: newSocial, 
                          social: newSocial 
                        }
                      }));
                    }}
                    className="text-red-600 hover:text-red-700 text-sm"
                  >
                    ✕
                  </button>
                </div>
              ))}
              
              <button
                onClick={() => {
                  const newSocial = [...(content?.footer?.socialLinks || content?.footer?.social || [])];
                  newSocial.push({ platform: '', url: '' });
                  setContent(prev => ({
                    ...prev,
                    footer: { ...prev.footer, socialLinks: newSocial, social: newSocial }
                  }));
                }}
                className="w-full bg-blue-100 text-blue-700 py-2 px-4 rounded-md hover:bg-blue-200 text-sm"
              >
                + Add Social Link
              </button>
            </div>
          </div>
        )}

        {activeSection === 'settings' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Settings Section</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Theme</label>
                <select
                  value={content?.settings?.theme || 'light'}
                  onChange={(e) => updateSection('settings', 'theme', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="minimal">Minimal</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Family</label>
                <select
                  value={content?.settings?.fontFamily || 'Inter'}
                  onChange={(e) => updateSection('settings', 'fontFamily', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                >
                  <option value="Inter">Inter</option>
                  <option value="Roboto">Roboto</option>
                  <option value="Arial">Arial</option>
                  <option value="Helvetica">Helvetica</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Favicon URL</label>
              <input
                type="text"
                value={content?.settings?.favicon || ''}
                onChange={(e) => updateSection('settings', 'favicon', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="/favicon.ico"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SEO Title</label>
              <input
                type="text"
                value={content?.settings?.seoTitle || ''}
                onChange={(e) => updateSection('settings', 'seoTitle', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="QuickLinks - Your Link-in-Bio Page"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SEO Description</label>
              <textarea
                value={content?.settings?.seoDescription || ''}
                onChange={(e) => updateSection('settings', 'seoDescription', e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="Create a stunning link-in-bio page in minutes."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Custom CSS</label>
              <textarea
                value={content?.settings?.customCSS || ''}
                onChange={(e) => updateSection('settings', 'customCSS', e.target.value)}
                rows={6}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white font-mono text-sm"
                placeholder="/* Add custom CSS here */"
              />
            </div>

            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.settings?.analytics || false}
                  onChange={(e) => updateSection('settings', 'analytics', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Enable Analytics</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.settings?.showPricing !== false}
                  onChange={(e) => updateSection('settings', 'showPricing', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Show Pricing Section</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={content?.settings?.maintenanceMode || false}
                  onChange={(e) => updateSection('settings', 'maintenanceMode', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Maintenance Mode</span>
              </label>
            </div>
          </div>
        )}

        {activeSection === 'navbar' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Navigation Section</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
              <input
                type="text"
                value={content?.navbar?.logo || ''}
                onChange={(e) => updateSection('navbar', 'logo', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                placeholder="/logo.svg"
              />
            </div>

            <div>
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={content?.navbar?.showAuth || false}
                  onChange={(e) => updateSection('navbar', 'showAuth', e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Show Authentication Links</span>
              </label>
            </div>

            <div>
              <h4 className="font-medium text-gray-800 mb-3">Navigation Links</h4>
              <div className="text-sm text-gray-600 mb-3">
                Navigation links are currently managed through the component code. 
                Advanced navigation editing will be available in a future update.
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
        <button
          onClick={saveContent}
          disabled={isSaving}
          className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default TempHomePageContentManager;
